import { SortBy } from '@modules/common/types';
import { keyframes } from '@mui/material';
import {
  DateTimeRange,
  TimePeriodUnit,
} from '../components/DateRangePicker/types';
import { ReasonCandidate } from '../components/ReasonCandidateSuggestion/types';

export const TRACKED_KEYWORD = 'Tracked Keyword';
export const TRACKED_KEYWORD_SENTIMENT = `${TRACKED_KEYWORD} Sentiment`;

export const INTERCOM_HELP_CENTER_DOMAIN = 'https://helpcenter.enterpret.com';
export const INTERCOM_HELP_CENTER_ARTICLES_PATH = `${INTERCOM_HELP_CENTER_DOMAIN}/en/articles`;

export const CHANGELOG_URL = 'https://enterpret.com/changelog';

export enum Widgets {
  Reason = 'Reason',
  TrackedKeyword = 'Entity',
  Source = 'Source',
  TrackedKeywordSentiment = 'EntitySentiment',
  TrackedKeywordCategory = 'EntityCategory',
  ReviewSentiment = 'ReviewSentiment',
  Language = 'Language',
  Metadata = 'Metadata',
  FeedbackVolume = 'FeedbackVolume',
}

export const USER_IDENTIFIER_METADATA_DISPLAY_TEXT = 'User ID';

export const COMMON = 'Common';

export const TIME = 'Time';

export const ENTERPRET = 'Enterpret';

export const LAST_5_YEARS: DateTimeRange = {
  type: 'last',
  unit: TimePeriodUnit.Month,
  value: 5 * 12, // 60 months = 5 years
};

export const EMPTY_ARRAY = [];

export const NOTIFICATION_TIMEOUT_LONG_6S = 6000;

export const NOTIFICATION_TIMEOUT_LONG_8S = 8000;

export const FIFTEEN_MINUTES = 15 * 60 * 1000;

export const SUPPORT_EMAIL = 'support@enterpret.com';

export const NLS = 'NLS';

export const NAV_PATHS = {
  THEMATIC_ANALYSIS: 'thematic-analysis',
  THEMATIC_ANALYSIS_VIEW_DATA: 'thematic-analysis/view-data',
  THEMATIC_ANALYSIS_VIEW_FEEDBACK: 'thematic-analysis/view-feedback',
  ANALYSIS: 'analysis',
  SETTINGS: 'settings',
  SETTINGS_INTEGRATIONS: 'settings/integrations',
  DASHBOARD: 'dashboard',
  NEW_DASHBOARD: 'dashboard/New-Dashboard',
  DASHBOARDS: 'dashboards',
  ARCHIVED_DASHBOARDS: 'archived-dashboards',
  REPORT: 'report',
  RECORD: 'record',
  TAXONOMY: 'taxonomy',
  TAXONOMY_THEME: 'taxonomy/theme',
  TAXONOMY_THEME_CREATE: 'taxonomy/theme/create',
  TAXONOMY_ENTITY: 'taxonomy/tracked-keyword',
  TAXONOMY_REASON: 'taxonomy/reason',
  TAXONOMY_MERGE_SUGGESTIONS: 'taxonomy/merge-suggestions',
  TAXONOMY_ADD_TRACKED_KEYWORD: 'taxonomy/tracked-keyword/add',
  LOGOUT: 'logout',
  LOGIN: 'login',
  LOGIN_LOADING: 'login/loading',
  ONBOARD_USER: 'onboard-user',
  WELCOME: 'welcome',
  SUPPORT: 'support',
  HOME: 'home',
  ENTITY: 'tracked-keyword',
  REASON: 'reason',
  UNSUBSCRIBE: 'unsubscribe',
  EXPLORE: 'explore',
  WISDOM: 'wisdom',
  SAVED_WISDOM_SLUG: 'wisdom/:savedSlug',
  ONBOARD_TENANT_ADD_INTEGRATIONS: 'onboard-tenant/add-integrations',
  ONBOARD_TENANT_SHOW_PROGRESS: 'onboard-tenant/progress',
  MANAGE_METADATA: 'manage-metadata',
  CONNECT_SLACK: 'settings/integrations/connect-slack',
  ADMIN_SETTINGS: 'admin-settings',
  INTERNAL_ADMIN_SETTINGS: 'settings/internal-admin-settings',
  WORKSPACE_SETTINGS: 'workspace-settings',
  OAUTH_TOKEN_PROVIDER: 'oauth/authorize',
  MY_SAVED_FEEDBACK: 'my-saved-feedback',
  SEARCH: 'explore/search',
  SEARCH_SLUG: 'explore/search/:savedSlug',
  FEED: 'explore/feed',
  FEED_SLUG: 'explore/feed/:savedSlug',
  QUANTIFY: 'explore/quantify',
  QUANTIFY_SLUG: 'explore/quantify/:savedSlug',
  COMPARE: 'explore/compare',
  COMPARE_SLUG: 'explore/compare/:savedSlug',
  DASHBOARD_SETTINGS: 'dashboard-settings',
  SELECT_TENANT: 'select-tenant',
  MANAGE_UNIFIED_FIELDS: '/manage-unified-fields',
  USER_ONBOARDING_ADD_INTERESTS: '/onboard-user/add-interests',
  USER_ONBOARDING_WELCOME: '/onboard-user/welcome',
  COLLECTION: 'collections',
  SAVED_COLLECTION: 'collections/:savedSlug',
};

export const API_BASE_URL = 'api-v2.enterpret.com';

export const REDIRECT_URL = `https://${API_BASE_URL}/redirect`;

export const PROXY_HOST_URL = `${API_BASE_URL}/proxy`;

export const DEFAULT_SORT_BY: SortBy = {
  field: 'value',
  isAsc: false,
};

// values taken from 'field' string from 'EqlField' type
export enum EqlFieldName {
  Entity = 'entity',
  Reason = 'reason',
  Source = 'source',
  Language = 'language',
  Category = 'category',
  UserSentiment = 'sentiment',
  EntitySentiment = 'entitySentiment',
  Summary = 'summary',
  Search = 'search',
  FeedbackRecords = 'feedbackRecords',
}

export const EMPTY_REASON_CANDIDATES_ARRAY: ReasonCandidate[] = [];

export const RETURN_PATH_QUERY_PARAM = 'return_to';

export const USE_PARENT_OBJECT = 'use-parent-object';

export const EXPORT_CHART_AS_IMAGE_WIDTH = 1080;

export const FREEMIUM_VISITOR_TAG = 'freemium_visitor';

export const PREMIUM_VISITOR_TAG = 'premium_visitor';

export const UNIFIED_FIELD = 'Unified Field';

export const UNIFIED_FIELD_HELP_CENTER_DOC =
  'https://helpcenter.enterpret.com/en/articles/8830079-unified-fields#h_c8e76d70e3';

export const FADE_IN_KEYFRAMES = keyframes`
0% { opacity: 0; }
100% { opacity: 1; }
`;

export const EXPAND_FADE_OUT_KEYFRAMES = keyframes`
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(9); opacity: 0; }
`;

export const SPIRAL_SHRINK_KEYFRAMES = keyframes`
0% {
  opacity: 1;
  transform: scale(1) rotate(0deg);
}
10% {
  opacity: 0.95;
  transform: scale(0.9) rotate(36deg);
}
20% {
  opacity: 0.9;
  transform: scale(0.8) rotate(72deg);
}
30% {
  opacity: 0.85;
  transform: scale(0.7) rotate(108deg);
}
40% {
  opacity: 0.8;
  transform: scale(0.6) rotate(144deg);
}
50% {
  opacity: 0.75;
  transform: scale(0.5) rotate(180deg);
}
60% {
  opacity: 0.7;
  transform: scale(0.4) rotate(216deg);
}
70% {
  opacity: 0.65;
  transform: scale(0.3) rotate(252deg);
}
80% {
  opacity: 0.6;
  transform: scale(0.2) rotate(288deg);
}
90% {
  opacity: 0.55;
  transform: scale(0.1) rotate(324deg);
}
100% {
  opacity: 0.5;
  transform: scale(0) rotate(360deg);
}
`;

export const SPRING_BOUNCE_KEYFRAMES = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1.1);
  }
  60% {
    opacity: 1;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }`;

export const SLIDE_FROM_TOP_KEYFRAMES = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const SLIDE_FROM_BOTTOM_KEYFRAMES = keyframes`
  0% {
    opacity: 0;
    transform: translateY(100%);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
  `;

export const SLIDE_FROM_LEFT_KEYFRAMES = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const SLIDE_FROM_RIGHT_KEYFRAMES = keyframes`
  from {
    opacity: 0;
    transform: translateX(50%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
